import { useEffect, useState, useCallback } from "react";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";
import { LOGO } from "../lib/constants";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const DISMISS_KEY = "haplap_install_hint_v1";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true));

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const ios = isIOS();
  const standalone = isStandalone();

  const maybeShowHint = useCallback(() => {
    try { if (!localStorage.getItem(DISMISS_KEY)) setShowBanner(true); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (standalone) return undefined;
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); maybeShowHint(); };
    const onInstalled = () => { setShowBanner(false); setDeferred(null); };
    const onTrigger = () => doInstall();
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("haplap:install", onTrigger);
    if (ios) maybeShowHint();
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("haplap:install", onTrigger);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standalone, ios, maybeShowHint]);

  const dismiss = () => {
    setShowBanner(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const doInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
      setShowBanner(false);
      return;
    }
    // iOS or browsers without the native prompt → show manual steps.
    setInstructionsOpen(true);
  };

  if (standalone) return null;

  return (
    <>
      {showBanner && (deferred || ios) && (
        <div data-testid="install-banner"
          className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] md:bottom-4 z-[70] px-3 pointer-events-none">
          <div className="pointer-events-auto mx-auto max-w-md bg-white border border-copper/25 rounded-2xl shadow-xl p-3.5 flex items-center gap-3">
            <img src={LOGO} alt="" className="w-11 h-11 rounded-xl object-cover shrink-0" />
            <div className="min-w-0 flex-1 text-left">
              <p className="font-serif font-semibold text-bronze text-sm leading-tight">Install Haplap</p>
              <p className="text-xs text-muted-foreground leading-snug">Add it to your home screen for a faster, app-like experience.</p>
            </div>
            <Button data-testid="install-banner-btn" size="sm" onClick={doInstall}
              className="rounded-full bg-copper hover:bg-copper/90 text-white shrink-0">
              <Download className="w-4 h-4 mr-1" /> Install
            </Button>
            <button data-testid="install-banner-close" onClick={dismiss}
              className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground shrink-0" aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={instructionsOpen} onOpenChange={setInstructionsOpen}>
        <DialogContent data-testid="install-instructions" className="sm:max-w-md rounded-3xl text-center flex flex-col items-center max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-bronze flex items-center justify-center gap-2">
              <Smartphone className="w-5 h-5 text-copper" /> Install Haplap
            </DialogTitle>
            <DialogDescription className="sr-only">Steps to add Haplap to your home screen.</DialogDescription>
          </DialogHeader>
          {ios ? (
            <div className="space-y-4 text-left w-full max-w-xs mx-auto">
              <p className="text-sm text-foreground/80 text-center">On your iPhone or iPad, add Haplap to your home screen in two taps:</p>
              <div className="flex items-center gap-3 bg-secondary/50 rounded-2xl p-3">
                <span className="w-8 h-8 rounded-full bg-copper/15 text-copper flex items-center justify-center shrink-0"><Share className="w-4 h-4" /></span>
                <p className="text-sm text-foreground/85">Tap the <b>Share</b> button in Safari's toolbar.</p>
              </div>
              <div className="flex items-center gap-3 bg-secondary/50 rounded-2xl p-3">
                <span className="w-8 h-8 rounded-full bg-copper/15 text-copper flex items-center justify-center shrink-0"><Plus className="w-4 h-4" /></span>
                <p className="text-sm text-foreground/85">Choose <b>Add to Home Screen</b>, then tap <b>Add</b>.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-left w-full max-w-xs mx-auto">
              <p className="text-sm text-foreground/80 text-center">To install Haplap on this device:</p>
              <div className="flex items-center gap-3 bg-secondary/50 rounded-2xl p-3">
                <span className="w-8 h-8 rounded-full bg-copper/15 text-copper flex items-center justify-center shrink-0 font-bold">⋮</span>
                <p className="text-sm text-foreground/85">Open your browser's menu (<b>⋮</b> or <b>⋯</b>).</p>
              </div>
              <div className="flex items-center gap-3 bg-secondary/50 rounded-2xl p-3">
                <span className="w-8 h-8 rounded-full bg-copper/15 text-copper flex items-center justify-center shrink-0"><Download className="w-4 h-4" /></span>
                <p className="text-sm text-foreground/85">Tap <b>Install app</b> or <b>Add to Home screen</b>.</p>
              </div>
            </div>
          )}
          <Button data-testid="install-instructions-close" onClick={() => setInstructionsOpen(false)}
            className="rounded-full bg-copper hover:bg-copper/90 text-white mt-2 w-full max-w-xs">
            Got it 💛
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
